import { useDispatch } from "react-redux";
import { Col, Dropdown, message, Row } from "antd";
import { useState, useRef, useEffect } from "react";

import Button from "@components/Button";
import Table from "@components/Table";
import {
  ErrorServices,
  shortTermIPApi,
  useDeleteShortTermIncentiveMutation,
  useFetchShortTermIncentivesByPlanQuery,
  useFetchShortTermIncentivesQuery,
  useUploadShortTermIncentivesMutation,
} from "@services";
import { getColumns } from "./config";
import AddShortTermIncentive from "./AddIncentive";
import { IShortTermIncentive } from "@types";
import ShortTermIncentiveVersions from "./Versions";
import Upload from "@components/Upload";
import { useTypedSelector } from "@hooks";
import { useParams } from "react-router-dom";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import SearchBox from "@/components/searchInput";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import CustomButton from "@/components/customButton";
import customErrorMessage from "@/components/CustomErrorMessage";
import UploadButton from "@/components/UploadButton";
import { DownOutlined } from "@ant-design/icons";
import useDebounce from "@/hooks/useDebounce";

const ShortTermIncentives = () => {
  const { company, role } = useTypedSelector(state => state.auth?.user!);
  const { plan_id } = useParams<{ plan_id?: string }>();
  const { id: company_id } = company!;
  const dispatch = useDispatch();
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);

  const shortTermIncentive_id = useRef<any>(null);
  const [selectedShortTermIncentive, setSelectedShortTermIncentive] =
    useState<IShortTermIncentive | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleForEdit, setIsVisibleForEdit] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [isVersionsModal, setIsVersionsModal] = useState(false);
  const [page, setPage] = useState(1);
  const [isUploadModal, setIsUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState({
    file: null,
    active: false,
  });
  const [uploadShortTermIncentives, { isLoading: isUploading }] =
    useUploadShortTermIncentivesMutation();
  const [deleteShortTermIncentive, { isLoading: isDeleting }] =
    useDeleteShortTermIncentiveMutation();
  const {
    data: incentivesData,
    isLoading,
    refetch,
  } = useFetchShortTermIncentivesQuery(
    {
      company_id,
      page,
      page_size: pageSize,
      sort_by: sortBy,
      order,
      search: debouncedSearch,
    },
    { skip: !!plan_id === true }
  );
  const { data: incentives, pagination: incentivesPagination } =
    incentivesData || {};
  const { data: incentivesByPlanData, isLoading: isFetchingPlanIncentives } =
    useFetchShortTermIncentivesByPlanQuery(
      {
        company_id,
        plan_id,
        page_size: pageSize,
        page,
      },
      { skip: !!plan_id === false }
    );
  const { data: incentivesByPlan, pagination: incentivesByPlanPagination } =
    incentivesByPlanData || {};

  const data = plan_id ? incentivesByPlan : incentives;

  const pagination = plan_id
    ? incentivesByPlanPagination
    : incentivesPagination;

  const editShortTermIncentive = (incentive: IShortTermIncentive) => {
    setSelectedShortTermIncentive(incentive);
    setIsVisibleForEdit(true);
  };

  const removeShortTermIncentive = async (id: number) => {
    try {
      shortTermIncentive_id.current = id;
      await deleteShortTermIncentive({ company_id, id });
      message.success("Short term incentive deleted successfully!");
    } catch (error) {
      ErrorServices(error);
    }
  };

  const handleUpload = async () => {
    const { file, active }: { file: any; active: boolean } = uploadFile;
    setIsUploadModal(false);
    try {
      const formData = new FormData();
      formData.append("attachment", file!, file?.name!);
      const res = await uploadShortTermIncentives({
        company_id,
        active,
        body: formData,
      }).unwrap();
      message.success(res?.message);
    } catch (error) {
      customErrorMessage(error?.message);
    }
  };
  useEffect(() => {
    refetch();
  }, []);

  const download = (id?: number) => {
    dispatch(
      shortTermIPApi.endpoints.downloadShortTermIncentives.initiate(
        {
          company_id,
          version_id: id || null,
        },
        {
          subscribe: false,
          forceRefetch: true,
        }
      )
    );
  };
  const handleEdit = (val: any) => { };

  const columns = getColumns({
    isDeleting,
    shortTermIncentive_id,
    removeShortTermIncentive,
    editShortTermIncentive,
    userRole: role,
  });


  // ✅ COLUMN STATE
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    columns.map(col => col.key)
  );

  // ✅ FILTERED COLUMNS
  const filteredColumns = columns.filter(col =>
    visibleColumnKeys.includes(col.key)
  );

  // custom overlay
  const columnOverlay = (
    <div
      style={{
        background: "#fff",
        padding: 8,
        borderRadius: 6,
        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
        minWidth: 240,
        maxHeight: 300,
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {columns.map((col) => {
        const isActionsColumn = col.key === "actions";
        const isChecked = visibleColumnKeys.includes(col.key);
        const isDisabled =
          isActionsColumn || (isChecked && visibleColumnKeys.length <= 3);

        return (
          <label
            key={col.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 8px",
              borderRadius: 4,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (isActionsColumn) return;

                if (isChecked) {
                  setVisibleColumnKeys((prev) =>
                    prev.filter((key) => key !== col.key)
                  );
                } else {
                  setVisibleColumnKeys((prev) => [...prev, col.key]);
                }
              }}
              style={{
                margin: 0,
                width: 16,
                height: 16,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {typeof col.title === "string"
                ? col.title.replace(/\b\w/g, c => c.toUpperCase())
                : col.title}
            </span>
          </label>
        );
      })}
    </div>
  );

  return (
    <>
      {isUploadModal && (
        <Upload
          title="Upload Short Term Incentives"
          onSubmit={handleUpload}
          isVisible={isUploadModal}
          setIsVisible={setIsUploadModal}
          file={uploadFile}
          setFile={setUploadFile}
        />
      )}
      {isVisible && (
        <AddShortTermIncentive
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          selectedShortTermIncentive={null}
          setSelectedShortTermIncentive={setSelectedShortTermIncentive}
        />
      )}
      {isVisibleForEdit && selectedShortTermIncentive?.id && (
        <AddShortTermIncentive
          isVisible={isVisibleForEdit}
          setIsVisible={() => {
            setIsVisibleForEdit(false);
            setSelectedShortTermIncentive(null);
          }}
          selectedShortTermIncentive={selectedShortTermIncentive}
          setSelectedShortTermIncentive={setSelectedShortTermIncentive}
          isEdit={true}
        />
      )}
      {isVersionsModal && (
        <ShortTermIncentiveVersions
          isVisible={isVersionsModal}
          setIsVisible={setIsVersionsModal}
          download={download}
        />
      )}

      <div className="bg-white p-4 sm:p-5">
        <div className="jobCreationBar bg-revampBlue px-4 py-6 rounded-lg flex justify-between items-center ">
          <div className="title text-white  font-medium text-3xl">
            Short Term Incentives
          </div>

          <div className="flex flex-row justify-between">
            {!plan_id ? (
              <>
                <UploadButton
                  role={role}
                  createPermission={permissions.CREATE_COMPANY_STI}
                  updatePermission={permissions.UPDATE_COMPANY_STI}
                  onClick={() => setIsUploadModal(true)}
                  isLoading={isUploading}
                />

                <CustomButton
                  onClick={() => download()}
                  btnName="Download"
                  customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                  iconPosition="right"
                >
                  <MdOutlineFileDownload
                    size={20}
                    className="text-white font-bold"
                  />
                </CustomButton>
              </>
            ) : null}

            <CustomButton
              onClick={() => setIsVersionsModal(true)}
              btnName="Versions"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
              iconPosition="left"
            ></CustomButton>

            {checkPermission(permissions.CREATE_COMPANY_STI) && (
              <CustomButton
                onClick={() => setIsVisible(true)}
                btnName="Create Short Term Incentive"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                iconPosition="left"
              ></CustomButton>
            )}

            <SearchBox
              placeholder="Type to search..."
              value={searchInput}
              onChange={value => {
                setSearchInput(value);
                setPage(1);
              }}
            />

            <div className="dropdown ml-4">

              <Dropdown
                overlay={columnOverlay}
                trigger={["click"]}
                placement="bottomLeft"
              // getPopupContainer={trigger => trigger.parentElement}
              >
                <CustomButton
                  btnName="Columns"
                  customClasses="shadow-md font-medium border py-1 px-4"
                >
                  <DownOutlined style={{ marginLeft: 6 }} />
                </CustomButton>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>



      <div className="jobCreationListing mt-10 bg-white px-14 py-6 rounded-lg">

        <Row>
          <Table
            scroll={1500}
            data={data}
            columns={filteredColumns}
            isLoading={isLoading || isFetchingPlanIncentives}
            pagination={true}
            count={pagination?.count}
            onChangePage={setPage}
            onPageSizeChange={setPageSize}
            page={page}
            onTableChange={(pagination, filters, sorter) => {
              const fieldMap: Record<string, string> = {
                grade: "grade",
                country: "country",
                is_percentage: "is_percentage",
                value: "value",
                is_basic_pay: "is_basic_pay",
              };

              const field = fieldMap[sorter.columnKey as string];

              if (field && sorter.order) {
                setSortBy(field);
                setOrder(sorter.order === "ascend" ? "asc" : "desc");
                setPage(1);
              }
            }}
          />
        </Row>
      </div>
    </>
  );
};

export default ShortTermIncentives;
